import { ArrowRightIcon } from '@heroicons/react/outline';

import clsxm from '@/lib/clsxm';

import MoneyFormat from '@/components/MoneyFormat';
import Typography from '@/components/typography';

import { Job } from '@/services/public-jobs.service';
import { routes } from '@/utils/route-utils';

export type SimpleJobListItemProps = Job;

export default function SimpleJobListItem({
  discipline,
  disciplineTitle,
  contractWeeks,
  location,
  simpleTitle,
  shift,
  weeklyPay,
  specialty,
  title,
  code,
  id,
}: SimpleJobListItemProps) {
  const jobUrl = routes.jobFull({
    discipline,
    specialty,
    title,
    code,
    id,
  });

  /*
    The DS typography component does not support responsive text, but this component
    wants it to be responsive. So we are hiding/showing the correct typography based
    on the screen size.  This is not ideal, but it works until the DS typography
    becomes responsive.
  */
  const showOnMediumScreen = 'hidden md:block';
  const showOnMobileScreen = 'md:hidden';

  return (
    <a
      className={clsxm(
        'flex cursor-pointer flex-row justify-between overflow-hidden bg-white',
        'gap-2 rounded-xl border border-solid border-primary-light px-4 pb-2 pt-4 md:gap-4 md:px-6'
      )}
      href={jobUrl}
    >
      <div className='flex flex-1 flex-col-reverse justify-between md:w-8/12 md:flex-row md:items-center md:gap-4 lg:w-7/12'>
        <div className='flex flex-col items-baseline justify-between md:w-60 md:gap-2'>
          <Typography.Heading6
            className={clsxm(
              'flex-1 !break-keep leading-6 text-grey800',
              showOnMediumScreen
            )}
          >
            {simpleTitle}
          </Typography.Heading6>
          <Typography.Body3
            className={clsxm(
              'flex-1 !break-keep leading-6 text-gray-800',
              showOnMobileScreen
            )}
          >
            {simpleTitle}
          </Typography.Body3>

          <Typography.Body2
            className={clsxm('leading-6 text-gray-800', showOnMediumScreen)}
          >
            {location?.city}, {location?.state}
          </Typography.Body2>

          <Typography.Body4
            className={clsxm('leading-6 text-gray-800', showOnMobileScreen)}
          >
            {location?.city}, {location?.state}
          </Typography.Body4>

          <Typography.Body4 className='hidden leading-4 text-gray-600 first-letter:uppercase sm:block'>
            {disciplineTitle} • {shift} • {contractWeeks} weeks
          </Typography.Body4>
        </div>

        <div className='mb-2 flex min-w-fit flex-row items-baseline gap-1 md:flex-1 md:justify-center'>
          <Typography.Heading6 className='text-primary-extra-dark'>
            <MoneyFormat value={weeklyPay} />
          </Typography.Heading6>
          <Typography.Body4 className='text-gray-600'>/ wk</Typography.Body4>
        </div>
      </div>

      <div className='mb-2 flex flex-row items-center gap-2'>
        <Typography.Body3
          className={clsxm(
            '!break-keep text-grey800 md:text-black',
            showOnMediumScreen
          )}
        >
          View Job Details
        </Typography.Body3>

        <Typography.Body4
          className={clsxm(
            'text-grey-800 !break-keep sm:text-black',
            showOnMobileScreen
          )}
        >
          View Job Details
        </Typography.Body4>
        <div className='rounded-full bg-primary-light p-1 sm:p-3'>
          <ArrowRightIcon className='stroke-primary-extra-dark' width={19} />
        </div>
      </div>
    </a>
  );
}
